<template>
  <!--
    Shoppable will not have user data mapping to start out in graphics, but will eventually.
    So I needed to add the data props but not show the mapping options for now.
    Since we add this in a lot of places it was easier to just add a v-if check here.
  -->
  <div
    v-if="dataProvider.type !== 'shoppable'"
    class="margin-bottom-sm"
    :data-test="`graphics-mapDataControl-${property}`"
    :data-entity="`graphics-mapDataControl-${property}`"
  >
    <div v-if="controlType === 'select'">
      <div>
        <label
          :for="property + element.id"
          class="sidebar-form-label"
        >{{ label }}</label>
      </div>
      <div>
        <!-- todo: make a generic data mapper -->
        <social-mapper
          v-if="dataProvider.type === 'social-data'"
          :select-name="property + element.id"
          :value="element.data.dataKeyMap[property]"
          :data-provider="dataProvider"
          :data-type="element.data.type"
          :property="property"
          control-type="select"
          @update-map="updateMap"
        />
      </div>
    </div>
    <div
      v-else
      class="flex flex-align-center flex-space-between"
    >
      <p
        class="text-capitalize text-xsmall no-margin ellipsis"
        :title="property"
      >
        {{ property }}
      </p>
      <button
        ref="dataProviderButton"
        :title="`${dataProvider ? 'Change' : 'Add'} Data Mapping`"
        class="btn btn-sm btn-icon btn-style-b font-weight-bold"
        :data-test="`graphics-mapDataControl-mapData-${property}`"
        :data-entity="`graphics-mapDataControl-mapData-${property}`"
        @click.stop="openDialog"
      >
        <span class="margin-right-xs fas fa-database" />
        Map Data
      </button>
    </div>

    <div
      v-if="mapped && !noUnmap"
      class="flex flex-center-all margin-top-sm"
    >
      <div class="sidebar__data-mapping-label flex-grow">
        {{ mappedDataInfo }}
      </div>
      <div>
        <button
          aria-label="Clear Mapping"
          aria-label-left
          class="btn btn-style-b btn-sm btn-icon btn-danger"
          data-test="graphics-mapDataControl-clearMapping"
          data-entity="graphics-mapDataControl-clearMapping"
          @click="clearMapping"
        >
          <span class="far fa-trash-alt" />
        </button>
      </div>
    </div>

    <div ref="dialogWrap">
      <template v-if="isModal">
        <sportradar-popup
          v-if="showPopup && dataProvider.type === 'sportradar'"
          :data-provider="dataProvider"
          :element="element"
          :property="property"
          @close="showPopup = false"
          @update-map="updateMap"
        />
      </template>
      <template v-else>
        <div
          v-if="showPopup"
          ref="mappingPopup"
          class="sidebar__data-selector"
          :style="dropdownStyle"
        >
          <sheets-popup
            v-if="dataProvider.type === 'sheet-data'"
            :data-provider="dataProvider"
            :property="property"
            @update-map="updateMap"
          />
          <social-mapper
            v-if="dataProvider.type === 'social-data'"
            :data-provider="dataProvider"
            :property="property"
            @update-map="updateMap"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash-es';
import { sheetColumn } from '../../../utils/filters';
import SheetsPopup from './GoogleSheets/MapPopup.vue';
import SportradarPopup from './Sportradar/ChangeData.vue';
import SocialMapper from './Social/DataMapper.vue';

export default {
  emits: ['clear-map', 'update-map'],
  name: 'MapDataControl',
  components: {
    SheetsPopup,
    SportradarPopup,
    SocialMapper,
  },
  props: {
    controlType: {
      type: String,
      default: 'button',
    },
    dataProvider: {
      type: Object,
      required: true,
    },
    scrollOffset: {
      type: Number,
      default: 0,
    },
    element: {
      type: Object,
      required: true,
    },
    property: {
      required: true,
      type: String,
    },
    mapped: {
      required: false,
      type: Boolean,
      default: false,
    },
    noUnmap: {
      required: false,
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      showPopup: false,
      dropdownStyle: '',
    };
  },
  computed: {
    mappedDataInfo() {
      switch (this.dataProvider.type) {
        case 'sheet-data':
          return `Column ${sheetColumn(
            this.element.data?.dataKeyMap?.[this.property] ?? this.element.data?.columnIdx,
          )} - ${this.dataProvider.name}`;
        case 'sportradar':
        case 'social-data':
          return `${this.element.data?.dataKeyMap?.[this.property]?.split('.').pop()} - ${
            this.dataProvider.name
          }`;
        default:
          return '';
      }
    },

    isModal() {
      return ['sportradar'].includes(this.dataProvider?.type);
    },
  },
  watch: {
    scrollOffset() {
      this.calcDropdownStyle();
    },
  },
  mounted() {
    this.debouncedHandleResize = debounce(this.calcDropdownStyle, 200);
    document.addEventListener('click', this.handleClickOutside);
    window.addEventListener('resize', this.debouncedHandleResize);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
    window.removeEventListener('resize', this.debouncedHandleResize);
  },
  methods: {
    viewportHeight() {
      return window.innerHeight;
    },
    popDivTopPosition() {
      const popDiv = this.$refs.mappingPopup;
      if (!popDiv) return 0;
      // Calculate the bottom position of the div
      return popDiv.offsetTop;
    },
    heightOfMappingPopup() {
      // FYI - it's really sad that this dropdown isn't a component.
      // Calculate the distance from the div's bottom to the bottom of the viewport
      const distanceToViewportBottom = this.viewportHeight() - this.popDivTopPosition();
      return distanceToViewportBottom - 10; // manual calc to give a bit of bottom margin
    },
    calcDropdownStyle() {
      const style = {
        marginTop: `-${this.scrollOffset}px`,
        maxHeight: `${this.heightOfMappingPopup()}px`,
      };

      if (this.$refs.dataProviderButton) {
        const r = this.$refs.dataProviderButton.getBoundingClientRect();
        style.right = `${window.innerWidth - r.right}px`;
      }

      this.dropdownStyle = style;
    },
    handleClickOutside(e) {
      // close dialog if click outside
      const { dialogWrap } = this.$refs;
      if (
        this.showPopup
        && dialogWrap
        && !dialogWrap.contains(e.target)
      ) {
        this.showPopup = false;
      }
    },
    clearMapping() {
      this.$emit('clear-map', this.property);
    },
    openDialog() {
      this.showPopup = !this.showPopup;
      this.$nextTick(() => {
        this.calcDropdownStyle();
      });
    },
    updateMap(data) {
      this.showPopup = false;
      this.$emit('update-map', data);
    },
  },
};
</script>
